import React, { useState, useEffect, useCallback } from 'react';
import { fetchActivityLog, fetchAllUsers } from '../api/thamanService';
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';

const ACTION_LABELS = {
    login: 'Logged In',
    logout: 'Logged Out',
    upload_flyer: 'Uploaded Flyer',
    delete_flyer: 'Deleted Flyer',
    create_user: 'Created User',
    deactivate_user: 'Deactivated User',
    activate_user: 'Activated User',
    reset_password: 'Reset Password',
};

const PAGE_SIZE = 50;

export default function ActivityLogPage() {
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterUserId, setFilterUserId] = useState('');
    const [offset, setOffset] = useState(0);

    const loadData = useCallback(async () => {
        try {
            setError(null);
            setIsLoading(true);
            const [logData, usersData] = await Promise.all([
                fetchActivityLog({ user_id: filterUserId || undefined, limit: PAGE_SIZE, offset }),
                users.length === 0 ? fetchAllUsers() : Promise.resolve(users),
            ]);
            setEntries(logData.entries);
            setTotal(logData.total);
            if (users.length === 0) setUsers(usersData);
        } catch (err) {
            setError('Failed to fetch activity log.');
        } finally {
            setIsLoading(false);
        }
    }, [filterUserId, offset, users]);

    useEffect(() => { loadData(); }, [loadData]);

    const formatDate = (d) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6">Activity Log</h1>

            {/* Filter */}
            <div className="flex items-center gap-4 mb-4">
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Filter by User</label>
                    <select
                        value={filterUserId}
                        onChange={e => { setFilterUserId(e.target.value); setOffset(0); }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Users</option>
                        {users.map(u => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                    </select>
                </div>
                <div className="text-sm text-gray-500 self-end pb-2">
                    {total} entries total
                </div>
            </div>

            {isLoading && <Loader message="Loading activity log..." />}
            {error && <ErrorMessage message={error} />}

            {!isLoading && !error && (
                <>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {entries.length === 0 ? (
                                        <tr><td colSpan="4" className="px-4 py-6 text-center text-gray-400">No activity recorded yet.</td></tr>
                                    ) : entries.map(entry => (
                                        <tr key={entry.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{entry.user_name}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-700">
                                                    {ACTION_LABELS[entry.action] || entry.action}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">{entry.details || '—'}</td>
                                            <td className="px-4 py-3 text-sm text-gray-500">{formatDate(entry.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-4">
                            <button
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                disabled={offset === 0}
                                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                            <button
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                                disabled={offset + PAGE_SIZE >= total}
                                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
